import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";

import type {
  ColumnWidthWeight,
  GapInlineStyle,
  GridWidthProps,
  LayoutAlign,
  LayoutGap,
} from "./types";
import { columnTracksTemplate, cx } from "./internal";
import { resolveGapLayout } from "./gap";
import { layoutAlignItemsClass } from "./size";
import {
  columnTrackSize,
  hasCustomColumnTracks,
  normalizeColumnWeight,
} from "./columns";
import { CMS_FLOW_CLASS } from "@/lib/cms-puck/block-style/defaults";

function layoutAlignSelfValue(align: LayoutAlign): string {
  return { start: "start", center: "center", end: "end", stretch: "stretch" }[
    align
  ];
}

/**
 * CSS injecté pour que chaque enfant Puck devienne une cellule de grille (BO).
 * `display: contents` sur le pont slot → les `[data-puck-component]` sont des
 * items directs ; `align-self` par enfant pour l'alignement vertical.
 */
export function gridChildrenAlignCss(
  scopeClass: string,
  verticalAlign: LayoutAlign,
  trackSizes?: string[],
): string {
  const self = layoutAlignSelfValue(verticalAlign);
  const childSel = `.${scopeClass} [data-puck-dropzone] > [data-puck-component]`;
  const rules = [
    `.${scopeClass} .cms-grid-bridge{display:contents}`,
    `.${scopeClass} .cms-grid-bridge [data-puck-dropzone]{display:contents}`,
    `${childSel}{align-self:${self};min-width:0}`,
  ];
  if (trackSizes?.length) {
    trackSizes.forEach((track, index) => {
      const nth = index + 1;
      if (track === "auto") {
        rules.push(`${childSel}:nth-child(${nth}){width:auto;max-width:100%}`);
      } else {
        rules.push(`${childSel}:nth-child(${nth}){width:100%}`);
      }
    });
  } else {
    rules.push(`${childSel}{width:100%}`);
  }
  return rules.join("");
}

/** Variante front public (pas de `data-puck-component`). */
export function gridPublicChildrenAlignCss(
  scopeClass: string,
  verticalAlign: LayoutAlign,
  trackSizes?: string[],
): string {
  const self = layoutAlignSelfValue(verticalAlign);
  const childSel = `.${scopeClass} .cms-grid-bridge > *`;
  const rules = [
    `.${scopeClass} .cms-grid-bridge{display:contents}`,
    `${childSel}{align-self:${self};min-width:0}`,
  ];
  if (trackSizes?.length) {
    trackSizes.forEach((track, index) => {
      const nth = index + 1;
      if (track === "auto") {
        rules.push(`${childSel}:nth-child(${nth}){width:auto;max-width:100%}`);
      } else {
        rules.push(`${childSel}:nth-child(${nth}){width:100%}`);
      }
    });
  } else {
    rules.push(`${childSel}{width:100%}`);
  }
  return rules.join("");
}

const GRID_WIDTH_KEYS = [
  "col1Width",
  "col2Width",
  "col3Width",
  "col4Width",
  "col5Width",
  "col6Width",
  "col7Width",
  "col8Width",
  "col9Width",
  "col10Width",
  "col11Width",
  "col12Width",
] as const;

/** Tailles de pistes pour une Grille (1–12 colonnes). */
export function resolveGridTrackSizes(
  columns: number,
  widths: GridWidthProps | ColumnWidthWeight[] | undefined,
): string[] {
  const n = Math.max(1, Math.min(MAX_CONTENT_COLUMNS, Math.floor(columns || 1)));
  if (Array.isArray(widths)) {
    return Array.from({ length: n }, (_, i) =>
      columnTrackSize(widths[i] ?? "1"),
    );
  }
  const map = (widths ?? {}) as Record<string, ColumnWidthWeight | undefined>;
  return GRID_WIDTH_KEYS.slice(0, n).map((key) =>
    columnTrackSize(normalizeColumnWeight(map[key])),
  );
}

/**
 * Layout du bloc Grid : colonnes égales par défaut, ou `grid-template-columns`
 * personnalisé (`auto`, parts `fr`…) si `columnWidths` / `col*Width` diffère.
 */
export function gridLayout(props: {
  columns: number;
  gap: LayoutGap;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
  columnWidths?: ColumnWidthWeight[];
  verticalAlign?: LayoutAlign;
} & GridWidthProps): {
  className: string;
  style: { gridTemplateColumns: string } & GapInlineStyle;
  customWidths: boolean;
} {
  const n = Math.max(1, Math.min(MAX_CONTENT_COLUMNS, Math.floor(props.columns || 1)));
  const trackSizes = props.columnWidths?.length
    ? resolveGridTrackSizes(n, props.columnWidths)
    : resolveGridTrackSizes(n, props);
  const custom = hasCustomColumnTracks(trackSizes);
  const gap = resolveGapLayout({
    gap: props.gap,
    gapCustomPx: props.gapCustomPx,
    gapRow: props.gapRow,
    gapCol: props.gapCol,
    gapRowCustomPx: props.gapRowCustomPx,
    gapColCustomPx: props.gapColCustomPx,
  });
  return {
    className: cx(
      "grid w-full",
      CMS_FLOW_CLASS,
      gap.className,
      layoutAlignItemsClass(props.verticalAlign ?? "stretch"),
    ),
    style: {
      gridTemplateColumns: custom
        ? columnTracksTemplate(trackSizes)
        : `repeat(${n}, minmax(0, 1fr))`,
      ...gap.style,
    },
    customWidths: custom,
  };
}

/**
 * Bloc Grid — alias historique (`repeat(N, 1fr)`). Préférer `gridLayout`.
 * @deprecated
 */
export function gridStyle(props: { columns: number; gap: LayoutGap }): {
  className: string;
  style: { gridTemplateColumns: string };
} {
  const layout = gridLayout(props);
  return { className: layout.className, style: layout.style };
}
